import { useEffect, useRef } from "react";
import Plyr from "plyr";
import Hls from "hls.js";
import type { StreamSource } from "@tih/shared";
import { useStreamPoster } from "../hooks/useStreamPoster";
import "plyr/dist/plyr.css";

type Props = {
  source: StreamSource;
  /** True for an active live broadcast (HLS w/ DVR or pure live). */
  live?: boolean;
  /** Optional poster image (overrides `source.poster`). */
  poster?: string | null;
  /** Auto-start (Plyr will autoplay only when muted; we set muted=true here). */
  autoPlay?: boolean;
  className?: string;
  title?: string;
  /** Optional YouTube clip start (seconds). */
  startSeconds?: number;
  /** Optional YouTube clip end (seconds). */
  endSeconds?: number;
};

const PLYR_BASE: Plyr.Options = {
  controls: [
    "play-large",
    "play",
    "progress",
    "current-time",
    "duration",
    "mute",
    "volume",
    "captions",
    "settings",
    "pip",
    "airplay",
    "fullscreen",
  ],
  settings: ["captions", "quality", "speed"],
  ratio: "16:9",
  hideControls: true,
  keyboard: { focused: true, global: false },
  tooltips: { controls: true, seek: true },
  youtube: {
    noCookie: true,
    rel: 0,
    showinfo: 0,
    iv_load_policy: 3,
    modestbranding: 1,
  },
  // VOD speed options; Plyr trims them to YT/Vimeo's 0.5–2 range automatically.
  speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] },
};

const PLYR_LIVE: Plyr.Options = {
  ...PLYR_BASE,
  // Live: keep progress for DVR/ended broadcasts, but drop duration/speed.
  controls: [
    "play-large",
    "play",
    "progress",
    "current-time",
    "mute",
    "volume",
    "captions",
    "settings",
    "pip",
    "airplay",
    "fullscreen",
  ],
  settings: ["captions", "quality"],
  speed: { selected: 1, options: [1] },
};

export function StreamPlayer({
  source,
  live = false,
  poster,
  autoPlay = false,
  className,
  title,
  startSeconds,
  endSeconds,
}: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Plyr | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const fallbackPoster = poster ?? source.poster ?? null;
  const resolvedPoster = useStreamPoster(source, fallbackPoster);

  // Build/teardown the player whenever the source identity changes.
  useEffect(() => {
    if (resolvedPoster === undefined) return;

    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;
    let mediaEl: HTMLVideoElement | HTMLDivElement | null = null;

    const posterUrl = resolvedPoster;
    const opts: Plyr.Options = {
      ...(live ? PLYR_LIVE : PLYR_BASE),
      autoplay: autoPlay,
      muted: autoPlay,
      ...(source.kind === "youtube"
        ? {
            youtube: {
              ...(PLYR_BASE.youtube ?? {}),
              ...(typeof startSeconds === "number" ? { start: startSeconds } : {}),
              ...(typeof endSeconds === "number" ? { end: endSeconds } : {}),
            },
          }
        : {}),
    };
    const applyPoster = (p: Plyr | null) => {
      if (p && posterUrl) {
        try { p.poster = posterUrl; } catch { /* not available for some providers */ }
      }
    };

    if (source.kind === "youtube") {
      const div = document.createElement("div");
      div.className = "plyr__video-embed";
      div.setAttribute("data-plyr-provider", "youtube");
      div.setAttribute("data-plyr-embed-id", source.src);
      host.appendChild(div);
      mediaEl = div;
      playerRef.current = new Plyr(div, opts);
      applyPoster(playerRef.current);
    } else {
      const video = document.createElement("video");
      video.setAttribute("playsinline", "");
      video.setAttribute("controls", "");
      video.setAttribute("crossorigin", "anonymous");
      if (posterUrl) video.setAttribute("poster", posterUrl);
      if (title) video.setAttribute("title", title);
      host.appendChild(video);
      mediaEl = video;

      if (source.kind === "hls") {
        // Safari + iOS: native HLS via <video src>.
        if (video.canPlayType("application/vnd.apple.mpegurl")) {
          video.src = source.src;
          playerRef.current = new Plyr(video, opts);
        } else if (Hls.isSupported()) {
          const hls = new Hls({
            lowLatencyMode: live,
            backBufferLength: live ? 30 : 90,
            enableWorker: true,
          });
          hlsRef.current = hls;
          hls.loadSource(source.src);
          hls.attachMedia(video);

          // Wait for manifest so Plyr can read renditions for its quality menu.
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            if (cancelled) return;
            const heights = Array.from(
              new Set(
                hls.levels
                  .map((l) => l.height)
                  .filter((h): h is number => Number.isInteger(h) && h > 0),
              ),
            ).sort((a, b) => b - a);

            playerRef.current = new Plyr(video, {
              ...opts,
              quality: heights.length
                ? {
                    default: heights[0],
                    options: heights,
                    forced: true,
                    onChange: (newQuality: number) => {
                      const idx = hls.levels.findIndex((l) => l.height === newQuality);
                      hls.currentLevel = idx; // -1 if not found → auto
                    },
                  }
                : undefined,
            });
          });

          hls.on(Hls.Events.ERROR, (_e, data) => {
            if (!data.fatal) return;
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
              default:
                hls.destroy();
            }
          });
        } else {
          // No HLS support at all. Surface the URL so the visitor can open it externally.
          video.src = source.src;
          playerRef.current = new Plyr(video, opts);
        }
      } else if (source.kind === "mp4") {
        video.src = source.src;
        playerRef.current = new Plyr(video, opts);
      }
    }

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch {
        /* noop */
      }
      playerRef.current = null;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      if (mediaEl?.parentNode === host) host.removeChild(mediaEl);
    };
    // We intentionally rebuild when src or kind changes; live/autoplay/poster
    // only matter at construction time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.kind, source.src, live, resolvedPoster, startSeconds, endSeconds]);

  return (
    <div
      ref={hostRef}
      className={["tih-plyr-host", className ?? ""].filter(Boolean).join(" ")}
    />
  );
}
